// src/Pages/conges/ConfirmerConge.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Mail,
  ShieldCheck,
  Info
} from 'lucide-react';

const ConfirmerConge = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [conge, setConge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationDetails, setValidationDetails] = useState(null);
  const [soldeEmploye, setSoldeEmploye] = useState({ normal: 0, exceptionnel: 0 });

  const [formData, setFormData] = useState({
    action: 'confirmation',
    duree_accordee: '',
    justification: '',
  });

  useEffect(() => {
    fetchCongeDetails();
  }, [id]);

  const fetchCongeDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/conges/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur chargement');
      
      const data = await response.json();
      setConge(data);

      const validation = data.validations?.find(v => v.role === 'validateur');
      setValidationDetails(validation);

      if (data.user?.id) {
        const soldeResponse = await fetch(`${API_URL}/solde-conges/${data.user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (soldeResponse.ok) {
          const soldeData = await soldeResponse.json();
          setSoldeEmploye(soldeData);
        }
      }

      const initialDuree = validation?.duree_accordee || data.duree || '';
      setFormData(prev => ({
        ...prev,
        duree_accordee: initialDuree,
      }));

    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
      navigate('/chef/conges');
    } finally {
      setLoading(false);
    }
  };

  const getSoldeDisponible = () => {
    if (!conge) return 0;
    return conge.type === 'normal' ? soldeEmploye.normal : soldeEmploye.exceptionnel;
  };

  const getSoldeApresConfirmation = () => {
    if (!conge || formData.action !== 'confirmation') return getSoldeDisponible();
    
    const dureeAccordee = parseInt(formData.duree_accordee) || 0;
    const dureeValidee = validationDetails?.duree_accordee || conge.duree;
    const diff = dureeAccordee - dureeValidee;
    
    return getSoldeDisponible() - diff;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.justification.trim()) {
      toast.error('Justification obligatoire');
      return;
    }

    if (formData.action === 'confirmation') {
      const duree = parseInt(formData.duree_accordee);
      
      if (isNaN(duree) || duree < 1 || duree > 22) {
        toast.error('Durée invalide (1-22 jours)');
        return;
      }

      const soldeApres = getSoldeApresConfirmation();
      if (soldeApres < 0) {
        toast.error(`Solde insuffisant. Disponible: ${getSoldeDisponible()} jours`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        action: formData.action === 'confirmation' ? 'confirmation' : 'refus',
        duree_accordee: formData.action === 'confirmation' ? parseInt(formData.duree_accordee) : 0,
        justification: formData.justification.trim(),
      };

      const response = await fetch(`${API_URL}/chef/conges/${id}/confirmer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Erreur traitement');

      toast.success(formData.action === 'confirmation' ? 'Congé confirmé' : 'Congé refusé');
      setTimeout(() => navigate('/chef/conges'), 1000);

    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du traitement');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!conge) {
    return (
      <div className="min-h-screen  flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Demande non trouvée</h3>
          <p className="text-gray-500 text-sm mb-6">
            La demande de congé que vous recherchez n'existe pas ou a été supprimée.
          </p>
          <button
            onClick={() => navigate('/chef/conges')}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Retour aux demandes
          </button>
        </div>
      </div>
    );
  }

  const soldeDisponible = getSoldeDisponible();
  const soldeApres = getSoldeApresConfirmation();
  const showSoldeWarning = formData.action === 'confirmation' && soldeApres < 0;

  return (
    <div className="min-h-screen  py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/chef/conges')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux demandes
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Confirmation de congé</h1>
              <p className="text-gray-500 text-sm mt-1">Demande #{conge.id}</p>
            </div>
            
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-full">
              <ShieldCheck className="w-4 h-4 mr-1.5" />
              Décision finale
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Employee Info */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Employé</h2>
              </div>
              
              <div className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{conge.user?.nom || '-'}</div>
                    {conge.user?.email && (
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Mail className="w-3 h-3 mr-2" />
                        {conge.user.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Details */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Détails du congé</h2>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Type</label>
                    <div className={`inline-flex px-3 py-1 text-sm rounded-full ${
                      conge.type === 'exceptionnel' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Durée demandée</label>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900">{conge.duree} jour{conge.duree > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-2">Période</label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span>Du {formatDate(conge.date_debut)} au {formatDate(conge.date_fin)}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-2">Motif</label>
                  <div className=" border border-gray-200 rounded p-4">
                    <p className="text-gray-700 whitespace-pre-line">{conge.motif || '-'}</p>
                  </div>
                </div>

                {/* Exception Details */}
                {conge.exception_details && conge.exception_details.length > 0 && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-3">
                      Jours exceptionnels ({conge.exception_details.length})
                    </label>
                    <div className="space-y-3">
                      {conge.exception_details.map((detail, index) => (
                        <div key={index} className="border-l-2 border-purple-500 pl-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {format(new Date(detail.jour), 'dd MMMM yyyy', { locale: fr })}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{detail.motif_jour}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Validation Details */}
            {validationDetails && (
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Validation RH</h2>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{validationDetails.user?.nom || 'Validateur'}</div>
                      <div className="text-sm text-gray-500">Validation RH</div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Durée accordée</label>
                    <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                      {validationDetails.duree_accordee} jours
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Justification</label>
                    <div className=" border border-gray-200 rounded p-4">
                      <p className="text-gray-700">{validationDetails.justification || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Decision Panel */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Décision finale</h2>
              </div>
              
              <div className="p-6">
                {/* Employee Balance */}
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-3">Solde de l'employé</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`text-center p-3 rounded-lg ${
                      conge.type === 'normal' ? 'bg-blue-50 border border-blue-100' : ' border border-gray-100'
                    }`}>
                      <div className="text-sm text-gray-600">Normaux</div>
                      <div className="text-lg font-semibold text-gray-900">{soldeEmploye.normal}j</div>
                    </div>
                    <div className={`text-center p-3 rounded-lg ${
                      conge.type === 'exceptionnel' ? 'bg-purple-50 border border-purple-100' : ' border border-gray-100'
                    }`}>
                      <div className="text-sm text-gray-600">Exceptionnels</div>
                      <div className="text-lg font-semibold text-gray-900">{soldeEmploye.exceptionnel}j</div>
                    </div>
                  </div>
                  
                  {formData.action === 'confirmation' && (
                    <div className={`mt-4 p-3 rounded-lg text-center ${
                      showSoldeWarning 
                        ? 'bg-red-50 border border-red-100 text-red-700' 
                        : 'bg-green-50 border border-green-100 text-green-700'
                    }`}>
                      <div className="text-xs">Après confirmation</div>
                      <div className="text-lg font-semibold">{soldeApres}j restants</div>
                    </div>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Action Choice */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-3">Action</label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`cursor-pointer border rounded-lg p-4 text-center transition-colors ${
                        formData.action === 'confirmation' 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="action"
                          value="confirmation"
                          checked={formData.action === 'confirmation'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            action: 'confirmation',
                            duree_accordee: validationDetails?.duree_accordee || conge.duree
                          }))}
                          className="sr-only"
                        />
                        <CheckCircle className={`w-5 h-5 mx-auto mb-2 ${
                          formData.action === 'confirmation' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div className="text-sm font-medium">Confirmer</div>
                      </label>
                      
                      <label className={`cursor-pointer border rounded-lg p-4 text-center transition-colors ${
                        formData.action === 'refus' 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <input
                          type="radio"
                          name="action"
                          value="refus"
                          checked={formData.action === 'refus'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            action: 'refus',
                            duree_accordee: '0'
                          }))}
                          className="sr-only"
                        />
                        <XCircle className={`w-5 h-5 mx-auto mb-2 ${
                          formData.action === 'refus' ? 'text-red-600' : 'text-gray-400'
                        }`} />
                        <div className="text-sm font-medium">Refuser</div>
                      </label>
                    </div>
                  </div>

                  {/* Duration (if confirming) */}
                  {formData.action === 'confirmation' && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">
                        Durée accordée (jours)
                      </label>
                      <input
                        type="number"
                        name="duree_accordee"
                        min="1"
                        max="22"
                        value={formData.duree_accordee}
                        onChange={(e) => setFormData(prev => ({ ...prev, duree_accordee: e.target.value }))}
                        className={`w-full px-3 py-2.5 text-sm border rounded focus:outline-none focus:border-blue-500 ${
                          showSoldeWarning ? 'border-red-300' : 'border-gray-300'
                        }`}
                        required
                      />
                      <div className="text-xs text-gray-500 mt-2">
                        Validateur: {validationDetails?.duree_accordee || conge.duree} jours
                      </div>
                    </div>
                  )}

                  {/* Justification */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Justification *
                    </label>
                    <textarea
                      name="justification"
                      value={formData.justification}
                      onChange={(e) => setFormData(prev => ({ ...prev, justification: e.target.value }))}
                      rows="4"
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      placeholder="Expliquez votre décision..."
                      required
                    />
                  </div>

                  {/* Submit */}
                  <div className="space-y-3">
                    <button
                      type="submit"
                      disabled={submitting || showSoldeWarning}
                      className={`w-full py-2.5 text-sm font-medium rounded ${
                        formData.action === 'confirmation'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Traitement...
                        </span>
                      ) : (
                        `Valider ${formData.action === 'confirmation' ? 'confirmation' : 'refus'}`
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => navigate('/chef/conges')}
                      className="w-full py-2.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Information */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Informations
                </h2>
              </div>
              
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>Votre décision est définitive</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>L'employé sera automatiquement notifié</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>Vérifiez le solde disponible avant confirmation</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                    <span>Justifiez clairement votre décision</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmerConge;